import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { SystemApp } from '../constans/SystemConfigurations';
import { getApps } from 'firebase/app';
import { getAuth, signOut } from 'firebase/auth';
import { TokenStore } from './token-store';
import { WebSocketService } from './web-socket.service';

/**
 * Owns the access-token lifecycle for the web client (POD1-8).
 *
 * `withCredentials: true` is mandatory on every call here - it is what makes
 * the browser attach the HttpOnly `sale4all_refresh` cookie. Without it the
 * refresh endpoint sees no credential and every session ends at the first
 * page reload.
 */
@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly refreshUrl = `${SystemApp.base_url_api}/api/auth/token/refresh/`;
  private readonly logoutUrl = `${SystemApp.base_url_api}/api/auth/logout/`;
  private readonly csrfUrl = `${SystemApp.base_url_api}/api/auth/csrf/`;

  /**
   * The in-flight refresh, if any.
   *
   * SINGLE-FLIGHT, and this matters: when several requests 401 at once (the
   * normal case - a page fires many calls on load), each would otherwise start
   * its own refresh. Because ROTATE_REFRESH_TOKENS is on, the second one
   * presents an already-rotated token, the backend reads that as reuse, and
   * POD1-9 chain revocation logs the user out entirely. One shared refresh
   * avoids turning an ordinary token expiry into a forced sign-out.
   */
  private inFlight$: Observable<string | null> | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    // Injector, not direct injection: WebSocketService pulls in ApiUrl and
    // HttpClient, and HttpClient depends on the interceptor chain - a
    // constructor dependency here is a cycle Angular cannot resolve.
    private injector: Injector,
  ) {}

  /**
   * True once the browser holds a `csrftoken` cookie.
   *
   * Read directly rather than tracked in a field: the cookie is set by a
   * `Set-Cookie` on the seed response, so there is no point in the TypeScript
   * where we could reliably assign it ourselves.
   */
  private hasCsrfCookie(): boolean {
    // SSR has no document, and no cookie jar to seed either.
    if (typeof document === 'undefined' || !document.cookie) {
      return false;
    }
    return document.cookie.split(';').some(c => c.trim().startsWith('csrftoken='));
  }

  /**
   * Fetch the CSRF cookie if we do not already have one (POD1-10).
   *
   * The refresh and logout endpoints reject a cookie-authenticated request
   * that carries no CSRF token. A browser that has just loaded the app has no
   * `csrftoken` cookie yet, so without this the very first bootstrap refresh
   * 403s and the user appears signed out on every reload.
   *
   * Always resolves. If the seed call fails, the refresh that follows will
   * fail on its own and be handled there - there is nothing useful to do with
   * the error here, and throwing would block app startup.
   */
  ensureCsrf(): Promise<void> {
    if (this.hasCsrfCookie()) {
      return Promise.resolve();
    }
    return this.http
      // 204 with no body; nothing here reads the result, only the Set-Cookie
      // it carries.
      .get(this.csrfUrl, { withCredentials: true })
      .toPromise()
      .then(() => undefined)
      .catch(() => undefined);
  }

  /**
   * POST to the refresh endpoint, one browser tab at a time.
   *
   * Every tab (and the admin panel) shares ONE HttpOnly refresh cookie, and
   * rotation means each refresh replaces it. When the browser is reopened all
   * restored tabs refresh at the same instant with the same cookie, and the
   * losers used to look like token theft to the server. A Web Lock makes
   * same-origin tabs queue: the next tab only sends its request after the
   * previous one has stored the new cookie. Browsers without Web Locks fall
   * back to a plain request (the server also tolerates a short overlap).
   */
  private lockedRefreshPost(): Observable<any> {
    const post = () => this.http
      .post<any>(this.refreshUrl, {}, { withCredentials: true })
      .toPromise();
    const locks = typeof navigator !== 'undefined' ? (navigator as any).locks : null;
    if (locks && typeof locks.request === 'function') {
      return from(locks.request('sale4all-token-refresh', post) as Promise<any>);
    }
    return from(post());
  }

  refresh(): Observable<string | null> {
    if (this.inFlight$) {
      return this.inFlight$;
    }

    this.inFlight$ = this.lockedRefreshPost()
      .pipe(
        map(res => (res && res.access) || null),
        tap(access => {
          TokenStore.set(access);
          this.inFlight$ = null;
        }),
        catchError(err => {
          this.inFlight$ = null;
          TokenStore.clear();
          // 401 TOKEN_REUSE_DETECTED means the session was revoked for
          // security reasons. It is NOT retryable - the user must sign in
          // again - so it is surfaced rather than swallowed.
          const code = err && err.error && err.error.statusCode;
          if (code === 'TOKEN_REUSE_DETECTED') {
            return throwError(err);
          }
          return of(null);
        }),
        // Late subscribers to the same refresh get the same result instead of
        // firing another request.
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.inFlight$;
  }

  /**
   * Called once at bootstrap. The access token never survives a reload, so
   * without this the user appears signed out on every refresh of the page.
   * Always resolves - a failure here must not block the app from starting.
   */
  restoreSession(): Promise<void> {
    if (!TokenStore.isLoggedIn()) {
      return Promise.resolve();
    }
    // POD1-10: the CSRF cookie MUST be in place before the refresh below,
    // otherwise the cookie-authenticated refresh is rejected with 403 and the
    // user is bounced to a signed-out state on every page load.
    return this.ensureCsrf()
      .then(() => this.refresh().toPromise())
      .then(() => undefined)
      .catch(() => undefined);
  }

  /**
   * Full logout (POD1-7).
   *
   * Order matters: the server call goes FIRST, while the refresh cookie is
   * still present. Clearing local state first would leave the session alive
   * server-side with no way to revoke it - exactly the stolen-token case this
   * epic exists to close.
   *
   * Every step is best-effort. A logout must never fail half-way and leave
   * the user apparently signed in; each step is recorded server-side
   * (POD1-11 `logout.step`) so a partial logout is visible.
   */
  logout(): Observable<any> {
    // POD1-10: logout is cookie-authenticated too, so it needs the CSRF token.
    // A user who signed in during THIS page load never went through
    // restoreSession(), so the cookie may not be seeded yet - without this,
    // their sign-out 403s, the refresh token is never revoked server-side, and
    // the session stays alive after the UI says it ended.
    return from(this.ensureCsrf()).pipe(
      switchMap(() => this.http
      // withCredentials sends the HttpOnly refresh cookie - without it the
      // server has no token to revoke and the session survives.
      .post(this.logoutUrl, {}, { withCredentials: true })
      .pipe(
        catchError(() => of(null)),
        tap(() => {
          // Drop the realtime connection. It authenticated once at connect,
          // so without this it keeps streaming this account's messages after
          // sign-out.
          try {
            const ws: any = this.injector.get(WebSocketService, null);
            if (ws && typeof ws.disconnect === 'function') {
              ws.disconnect();
            }
          } catch (e) {
            // never block a logout on socket teardown
          }
          TokenStore.clear();
          // 2026-10-24: end the Firebase (Google) session too, so the next
          // Google button press asks again instead of silently reusing it.
          // Only when the app already initialised Firebase; never on the server.
          // (The website registers no FCM push token, so there is no
          // fcm_token to send with the logout call.)
          try {
            if (typeof window !== 'undefined' && getApps().length) {
              signOut(getAuth(getApps()[0])).catch(() => undefined);
            }
          } catch (e) {
            // never block a logout on Firebase
          }
        })
      )),
    );
  }

  /** Logout, then return to Home regardless of the server result. */
  logoutAndGoHome(): void {
    this.logout().subscribe({
      next: () => this.router.navigateByUrl('/'),
      error: () => this.router.navigateByUrl('/'),
    });
  }
}

/** APP_INITIALIZER factory - see app.module.ts. */
export function restoreSessionFactory(auth: AuthSessionService) {
  return () => auth.restoreSession();
}
